/**
 * Flussonic Error Serializer
 *
 * Extracts meaningful error information from Flussonic player error objects,
 * which come in various shapes (plain objects, DOMExceptions, MediaErrors, strings, etc.).
 *
 * Without this, `new Error(err)` on an object produces "[object Object]" in Sentry.
 */

const MEDIA_ERROR_CODES: Record<number, string> = {
  1: "MEDIA_ERR_ABORTED",
  2: "MEDIA_ERR_NETWORK",
  3: "MEDIA_ERR_DECODE",
  4: "MEDIA_ERR_SRC_NOT_SUPPORTED",
};

const MAX_MESSAGE_LENGTH = 500;

interface SerializedError {
  message: string;
  details: Record<string, unknown>;
}

/**
 * Serializes a Flussonic error (of any shape) into a human-readable message and structured details.
 */
export function serializeFlussonicError(err: unknown): SerializedError {
  if (err === null || err === undefined) {
    return {
      message: "Unknown Flussonic error (no error data)",
      details: { rawValue: err },
    };
  }

  // String errors — use directly
  if (typeof err === "string") {
    const trimmed = err.trim();
    return {
      message: trimmed || "Empty string error",
      details: trimmed !== err ? { rawLength: err.length, hadWhitespace: true } : {},
    };
  }

  // DOMException (e.g. AbortError, InvalidStateError)
  if (err instanceof DOMException) {
    return {
      message: `${err.name}: ${err.message}`,
      details: { code: err.code, name: err.name },
    };
  }

  // Standard Error objects
  if (err instanceof Error) {
    if (err.message && err.message !== "[object Object]") {
      return {
        message: err.message,
        details: { name: err.name, stack: err.stack?.substring(0, 500) },
      };
    }
    // Error with [object Object] or empty message — extract own enumerable properties
    const ownProps = Object.getOwnPropertyNames(err).filter(
      (k) => k !== "stack" && k !== "message" && k !== "name"
    );
    const propsStr = ownProps
      .map(
        (k) =>
          `${k}=${JSON.stringify((err as unknown as Record<string, unknown>)[k])?.substring(0, 50)}`
      )
      .join(", ");
    return {
      message: propsStr
        ? `${err.name || "Error"} (${propsStr})`
        : `${err.name || "Error"} (no useful message)`,
      details: {
        name: err.name,
        stack: err.stack?.substring(0, 500),
        ownProperties: ownProps,
      },
    };
  }

  // MediaError (from HTMLMediaElement.error)
  if (isMediaError(err)) {
    const codeName = MEDIA_ERROR_CODES[err.code] || `UNKNOWN_CODE_${err.code}`;
    return {
      message: `MediaError ${codeName}: ${err.message || "no message"}`,
      details: { code: err.code, codeName },
    };
  }

  // Flussonic-style error objects: { error: string, err?: Error, mediaError?: MediaError }
  if (typeof err === "object") {
    const obj = err as Record<string, unknown>;

    // Common Flussonic shape: { error: "play_promise_reject", err: DOMException }
    if (typeof obj.error === "string" || typeof obj.message === "string") {
      const primary =
        (typeof obj.error === "string" && obj.error) ||
        (typeof obj.message === "string" && obj.message) ||
        "Unknown error";

      const details: Record<string, unknown> = {};
      let secondary = "";

      // Extract nested error
      if (obj.err) {
        const nested = serializeFlussonicError(obj.err);
        secondary = ` — ${nested.message}`;
        details.nestedError = nested.details;
      }

      // Extract nested MediaError
      if (obj.mediaError) {
        const nested = serializeFlussonicError(obj.mediaError);
        secondary += ` — ${nested.message}`;
        details.mediaError = nested.details;
      }

      return {
        message: truncate(`${primary}${secondary}`),
        details,
      };
    }

    // Unknown object — try JSON.stringify
    try {
      const json = JSON.stringify(err);
      return {
        message: truncate(`Flussonic error: ${json}`),
        details: { rawJson: json?.substring(0, 1000) },
      };
    } catch {
      return {
        message: "Flussonic error (unserializable object)",
        details: { type: typeof err, keys: Object.keys(obj) },
      };
    }
  }

  // Primitive fallback
  return {
    message: `Flussonic error: ${String(err)}`,
    details: { rawValue: err },
  };
}

/**
 * Wraps serializeFlussonicError into a proper Error object suitable for Sentry.captureException.
 */
export function toSentryError(err: unknown): Error {
  // If it's already an Error with a real message, return as-is
  if (err instanceof Error && err.message && err.message !== "[object Object]") {
    return err;
  }

  const { message } = serializeFlussonicError(err);
  if (!message) {
    try {
      return new Error(
        `Unknown Flussonic error: ${JSON.stringify(err).substring(0, 200)}`
      );
    } catch {
      return new Error("Unknown Flussonic error (unserializable)");
    }
  }
  return new Error(message);
}

function truncate(str: string): string {
  if (str.length <= MAX_MESSAGE_LENGTH) return str;
  return str.substring(0, MAX_MESSAGE_LENGTH) + "…";
}

function isMediaError(err: unknown): err is MediaError {
  return (
    typeof err === "object" &&
    err !== null &&
    "code" in err &&
    typeof (err as MediaError).code === "number" &&
    (err as MediaError).code >= 1 &&
    (err as MediaError).code <= 4
  );
}
